import { useCallback } from 'react';
import { useGetTheme } from '../api/useGetTheme';
import { cn } from '../lib/utils';
import { sdkBridge } from '../sdk-bridge';
import { getBackgroundStyle } from '../utils/theme';

type ButtonLauncherProps = {
  organizationId: string;
  agentId: string;
};

const StradaBubbleIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className} viewBox="0 0 239 240" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M119.248 0.618164C53.6651 0.618164 0.317505 54.1681 0.317505 120C0.317505 185.832 53.677 239.382 119.248 239.382C184.82 239.382 238.179 185.832 238.179 120C238.179 54.1681 184.832 0.618164 119.248 0.618164ZM196.076 52.9698L126.996 122.301C124.226 125.081 119.726 125.081 116.944 122.289C115.619 120.959 114.879 119.173 114.879 117.268C114.879 115.363 115.619 113.577 116.956 112.235L186.025 42.9045C189.606 46.008 192.961 49.3751 196.076 52.9698ZM119.248 222.223C63.0955 222.223 17.4116 176.366 17.4116 120C17.4116 71.0874 51.8029 30.1072 97.5941 20.1137L58.6906 59.1528C25.2902 92.6919 25.2902 147.26 58.6906 180.787C74.8776 197.036 96.3885 205.987 119.284 205.987C142.168 205.987 163.691 197.036 179.866 180.787L218.757 141.748C208.813 187.701 167.976 222.223 119.248 222.223ZM220.978 115.255L167.785 168.649C154.833 181.662 137.608 188.816 119.284 188.816C100.96 188.816 83.735 181.65 70.7831 168.637C44.0436 141.796 44.0436 98.12 70.7831 71.2672L123.975 17.873C130.075 18.1486 136.044 18.9754 141.822 20.2935L82.016 80.3379C71.7619 90.6309 66.1156 104.315 66.1156 118.862C66.1156 133.409 71.7619 147.093 82.016 157.386C92.5924 168.002 106.487 173.31 120.382 173.31C134.277 173.31 148.172 168.002 158.761 157.386L218.566 97.3411C219.879 103.141 220.691 109.132 220.978 115.255ZM212.574 79.0918L146.68 145.247C132.176 159.794 108.6 159.794 94.1085 145.247C87.0894 138.202 83.2217 128.831 83.2217 118.862C83.2217 108.892 87.0894 99.522 94.1085 92.4762L160.002 26.3327C164.18 28.166 168.215 30.2749 172.071 32.6355L104.876 100.097C100.304 104.686 97.7971 110.774 97.7971 117.268C97.7971 123.775 100.316 129.886 104.876 134.439C109.591 139.172 115.786 141.545 121.982 141.545C128.177 141.545 134.373 139.172 139.088 134.439L206.295 66.9894C208.646 70.8598 210.735 74.8979 212.574 79.0918Z"
      fill={color}
    />
    <path
      d="M178.927 217.192L218.231 177.888L218.939 185.505C220.02 197.142 222.373 208.626 225.956 219.75L226.816 222.419C227.483 224.491 225.474 226.422 223.43 225.674C210 220.755 195.892 217.934 181.603 217.309L178.927 217.192Z"
      fill={color}
    />
  </svg>
);

export const ButtonLauncher = ({ organizationId, agentId }: ButtonLauncherProps) => {
  // Fetch theme from API (with agentId for agent-specific configurations)
  const { data: themeData, isLoading, isError } = useGetTheme(organizationId, agentId);

  // Measure button on mount and send size to parent SDK
  const buttonRef = useCallback((node: HTMLButtonElement | null) => {
    if (!node) return;

    // Wait for next frame to ensure button is fully rendered
    requestAnimationFrame(() => {
      const rect = node.getBoundingClientRect();
      // Add padding to account for button positioning (right-6 bottom-6 = 24px) and hover scale
      const width = Math.ceil(rect.width + 24 + 12); // 24px positioning + 12px buffer for scale
      const height = Math.ceil(rect.height + 24 + 12); // 24px positioning + 12px buffer for scale

      sdkBridge.sendEvent('strada:button:resize', { width, height });
    });
  }, []);

  // Don't render button if theme is loading or failed to load
  if (isLoading || isError || !themeData) {
    return null;
  }

  const theme = themeData;

  const handleClick = () => {
    // Notify parent SDK to show the chat iframe
    sdkBridge.sendEvent('strada:button:clicked', {});
  };

  const hasText = theme.buttonText && theme.buttonText.trim() !== '';
  const isBTIS = organizationId === 'organization-live-56a5b20e-478d-4d0c-9114-fcbcc28166bd';

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className={cn(
        'fixed z-[9999] transition-all duration-200 hover:opacity-90 hover:scale-110',
        'right-6 bottom-6 flex items-center justify-center gap-2',
        isBTIS
          ? 'h-14 w-14 p-0 overflow-visible'
          : hasText
            ? 'h-11 px-6 rounded-full shadow-lg hover:shadow-xl'
            : 'h-14 w-14 rounded-full shadow-lg hover:shadow-xl'
      )}
      style={{
        ...(isBTIS ? { backgroundColor: 'transparent' } : getBackgroundStyle(theme.buttonColor)),
        color: theme.buttonTextColor
      }}
      aria-label={theme.buttonText || 'Open chat'}
    >
      {isBTIS ? (
        <img
          src="https://chat.btisinc.com/system/templates/chat/btis_new/custom/media/icon-chat-launch-1.svg"
          className="h-full w-full object-contain"
          style={{ display: 'block' }}
        />
      ) : (
        <StradaBubbleIcon color={theme.buttonTextColor} className="h-5 w-5" />
      )}
      {hasText && !isBTIS && <span className="font-semibold text-base">{theme.buttonText}</span>}
    </button>
  );
};
